<template>
  <div class="home-page">
    <div class="header">
      <h1 class="page-title">{{ $t('homePage.systemMonitorPanel') }}</h1>
      <div class="refresh-controls">
        <span class="last-update">{{ $t('homePage.lastUpdate') }}: {{ lastUpdateTime || '--:--:--' }}</span>
        <button
          class="refresh-btn"
          @click="manualRefresh"
          :disabled="loading"
          :class="{ 'refreshing': loading }"
        >
          <i class="refresh-icon" :class="{ 'spin': loading }">↻</i>
          {{ loading ? $t('homePage.refreshing') : $t('homePage.manualRefresh') }}
        </button>
        <button
          class="log-btn"
          @click="showLogDialog"
        >
          <i class="log-icon">📋</i>
          <span class="last-update">{{ $t('homePage.viewLogs') }}</span>
        </button>
      </div>
    </div>

    <div class="dashboard-grid">
      <!-- 系统信息卡片 -->
      <div class="info-card system-card">
        <div class="card-header">
          <h2 class="title">
            <i class="card-icon">🖥️</i>
            {{ $t('homePage.systemInfo') }}
          </h2>
          <div class="card-actions">
            <span class="uptime" v-if="hostInfo.Uptime">{{ $t('homePage.runningTime') }}: {{ hostInfo.Uptime }}</span>
          </div>
        </div>

        <div class="info-grid">
          <div class="info-item">
            <div class="label">{{ $t('homePage.hostName') }}</div>
            <div class="value highlight">{{ systemInfo }}</div>
          </div>

          <div class="info-item">
            <div class="label">{{ $t('homePage.cpuUsage') }}</div>
            <div class="value">
              <progress-bar
                :percentage="parseFloat(hostInfo.CpuPercent)"
                color="#FF6B6B"
              />
              <span class="percent">{{ hostInfo.CpuPercent }}</span>
            </div>
          </div>

          <div class="info-item">
            <div class="label">{{ $t('homePage.memoryUsage') }}</div>
            <div class="value">
              <progress-bar
                :percentage="parseFloat(memInfo.UsedPercent)"
                color="#4ECDC4"
              />
              <span class="detail">{{ memInfo.Used }}/{{ memInfo.Total }} ({{ memInfo.UsedPercent }})</span>
            </div>
          </div>

          <div class="info-item network-item">
            <div class="label">{{ $t('homePage.networkSpeed') }}</div>
            <div class="value">
              <div class="network-speed">
                <span class="speed upload">↑ {{ formatSpeed(hostInfo.SendSpeed) }}</span>
                <span class="speed download">↓ {{ formatSpeed(hostInfo.RecvSpeed) }}</span>
              </div>
            </div>
          </div>

          <div class="info-item">
            <div class="label">{{ $t('homePage.systemTime') }}</div>
            <div class="value clock">{{ currentTime }}</div>
          </div>

          <div class="info-item disk-item" v-for="disk in diskInfo" :key="disk.Path">
            <div class="label">{{ $t('homePage.disk') }} {{ disk.Path }}</div>
            <div class="value">
              <progress-bar
                :percentage="parseFloat(disk.UsedPercent)"
                color="#FFA07A"
              />
              <div class="disk-details">
                <span class="space">{{ disk.Used }} / {{ disk.Total }}</span>
                <span class="percent">{{ disk.UsedPercent }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 软件信息卡片 -->
      <div class="info-card app-card">
        <div class="card-header">
          <h2 class="title">
            <i class="card-icon">📊</i>
            {{ $t('homePage.softwareInfo') }}
          </h2>
          <div class="card-actions">
            <span class="uptime">{{ $t('homePage.runningTime') }}: {{ runningTime }}</span>
          </div>
        </div>

        <div class="info-grid">
          <div class="info-item">
            <div class="label">{{ $t('homePage.softwareVersion') }}</div>
            <div class="value">{{ version }}</div>
          </div>

          <div class="info-item">
            <div class="label">{{ $t('homePage.cpuUsageShort') }}</div>
            <div class="value">
              <progress-bar
                :percentage="parseFloat(pidInfo.CpuUsed)"
                color="#FFD166"
                small
              />
              <span class="percent">{{ pidInfo.CpuUsed }}</span>
            </div>
          </div>

          <div class="info-item">
            <div class="label">{{ $t('homePage.memoryUsageShort') }}</div>
            <div class="value">{{ pidInfo.MemUsed }}</div>
          </div>

          <div class="info-item">
            <div class="label">{{ $t('homePage.goroutineCount') }}</div>
            <div class="value">{{ pidInfo.GoNum }}</div>
          </div>

          <div class="info-item">
            <div class="label">{{ $t('homePage.startTime') }}</div>
            <div class="value">{{ startupTime }}</div>
          </div>

        </div>
      </div>
    </div>

    <!-- 系统日志查看对话框 -->
    <el-dialog
      v-model="logDialogVisible"
      :title="$t('homePage.runningLogs')"
      :width="isMobile ? '95%' : '900px'"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      class="settings-dialog log-dialog"
      append-to-body
    >
      <div class="log-toolbar">
        <div class="log-stats">
          <span>{{ $t('homePage.totalLogs', { count: logTotal }) }}</span>
          <div class="auto-refresh-control">
            <span class="refresh-label">{{ $t('homePage.autoRefresh') }}</span>
            <el-switch
              v-model="autoRefreshEnabled"
              :active-value="true"
              :inactive-value="false"
              @change="toggleAutoRefresh"
              size="small"
              active-color="#409eff"
            />
            <span class="refresh-interval" v-if="autoRefreshEnabled">{{ $t('homePage.refreshInterval') }}</span>
          </div>
        </div>
        <div class="log-actions">
          <el-button
            type="success"
            size="small"
            @click="downloadAllLogs"
            :loading="logDownloading"
          >
            <i class="download-icon">↓</i> {{ $t('homePage.downloadAllLogs') }}
          </el-button>
          <el-button
            type="primary"
            size="small"
            @click="refreshLogs"
            :loading="logLoading"
          >
            <i class="refresh-icon" :class="{ 'spin': logLoading }">↻</i> {{ $t('homePage.refreshNow') }}
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="clearLogs"
            :loading="logClearing"
          >
            <i class="clear-icon">🗑️</i> {{ $t('homePage.clearLogs') }}
          </el-button>
        </div>
      </div>

      <div class="log-content" ref="logContentRef">
        <div v-if="logsData.length === 0" class="empty-log">
          <i class="empty-icon">📭</i>
          <p>{{ $t('homePage.noLogs') }}</p>
        </div>
        <div v-else class="log-list">
          <div
            v-for="(log, index) in logsData"
            :key="index"
            class="log-item"
            :class="`log-level-${log.Level.toLowerCase()}`"
          >
            <span class="log-time">{{ log.Time }}</span>
            <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
              [{{ log.Level }}]
            </span>
            <span class="log-message">{{ log.Content }}</span>
          </div>
        </div>
      </div>

      <div class="log-pagination" v-if="logTotal > 0">
        <el-pagination
          v-model:current-page="logCurrentPage"
          v-model:page-size="logPageSize"
          :page-sizes="[20, 50, 100, 200]"
          :total="logTotal"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleLogSizeChange"
          @current-change="handleLogPageChange"
          small
        />
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="logDialogVisible = false">{{ $t('homePage.close') }}</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { API_BASE_URL } from '@/config';
import { ElMessage, ElMessageBox } from 'element-plus';

// 进度条组件
const ProgressBar = {
  props: {
    percentage: {
      type: Number,
      default: 0
    },
    color: {
      type: String,
      default: '#4ECDC4'
    },
    small: {
      type: Boolean,
      default: false
    }
  },
  template: `
    <div class="progress-container" :class="{ 'small': small }">
      <div 
        class="progress-bar" 
        :style="{
          width: Math.min(100, Math.max(0, percentage)) + '%',
          backgroundColor: color
        }"
      ></div>
    </div>
  `
};

export default {
  name: 'HomePage',
  components: {
    ProgressBar
  },
  data() {
    return {
      version: '3.4.2',
      systemInfo: '',
      currentTime: '',
      diskInfo: [],
      hostInfo: {
        Platform: '',
        KernelArch: '',
        Uptime: '',
        BootTime: '',
        CpuPercent: '0%',
        SendSpeed: '0',
        RecvSpeed: '0'
      },
      memInfo: {
        Total: '0GB',
        Used: '0GB',
        UsedPercent: '0%'
      },
      pidInfo: {
        CpuUsed: '0%',
        MemUsed: '0MB',
        GoNum: '0'
      },
      startupTime: '',
      runningTime: '',
      loading: false,
      refreshInterval: null,
      lastUpdateTime: null,
      // 日志相关
      logDialogVisible: false,
      logLoading: false,
      logClearing: false,
      logDownloading: false,
      logsData: [],
      logTotal: 0,
      logCurrentPage: 1,
      logPageSize: 50,
      autoRefreshEnabled: true,
      logRefreshInterval: null,
      isMobile: window.innerWidth < 768
    }
  },
  created() {
    this.fetchHomeInfo();
    this.setupAutoRefresh();
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
  },
  beforeUnmount() {
    this.clearAutoRefresh();
    this.stopLogAutoRefresh();
    window.removeEventListener('resize', this.checkMobile);
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },
    setupAutoRefresh() {
      this.refreshInterval = setInterval(() => {
        if (!this.loading) {
          this.fetchHomeInfo();
        }
      }, 3000);
    },
    clearAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    },
    async manualRefresh() {
      await this.fetchHomeInfo();
    },
    async fetchHomeInfo() {
      this.loading = true;
      try {
        const response = await fetch(`${API_BASE_URL}/get-homeInfo`, {
          credentials: 'include'
        });
        const data = await response.json();

        if (data.errCode === 1000) {
          ElMessage.warning(this.$t('homePage.pleaseLoginFirst'));
          this.$router.push('/user-login');

          return;
        }

        if (data.errCode && data.errCode !== 0) {
          throw new Error(data.errMsg || this.$t('homePage.fetchInfoFailed'));
        }

        this.version = data.version || this.version;
        this.systemInfo = data.systemInfo || `${data.hostInfo.Platform} ${data.hostInfo.KernelArch}`;
        this.currentTime = data.currentTime;
        this.diskInfo = data.diskInfo || [];
        this.hostInfo = data.hostInfo;
        this.memInfo = data.memInfo;
        this.pidInfo = data.pidInfo;
        this.startupTime = data.startupTime;
        this.runningTime = data.runningTime;
        this.lastUpdateTime = new Date().toLocaleTimeString();

      } catch (error) {
        console.error(this.$t('homePage.fetchInfoFailed') + ':', error);
        ElMessage.error(error.message || this.$t('homePage.fetchInfoFailed'));
      } finally {
        this.loading = false;
      }
    },
    formatSpeed(speed) {
      if (speed === '0' || !speed) return '0KB/s';
      const num = parseFloat(speed);
      if (num < 1024) return `${num.toFixed(2)}KB/s`;
      return `${(num / 1024).toFixed(2)}MB/s`;
    },
    // 显示日志对话框
    showLogDialog() {
      this.logDialogVisible = true;
      this.fetchLogs();
      // 开启日志自动刷新（如果开启状态）
      if (this.autoRefreshEnabled) {
        this.startLogAutoRefresh();
      }
    },
    // 切换自动刷新
    toggleAutoRefresh(enabled) {
      if (enabled) {
        this.startLogAutoRefresh();
        ElMessage.success(this.$t('homePage.autoRefreshEnabled'));
      } else {
        this.stopLogAutoRefresh();
        ElMessage.info(this.$t('homePage.autoRefreshDisabled'));
      }
    },
    // 启动日志自动刷新
    startLogAutoRefresh() {
      this.stopLogAutoRefresh(); // 先停止已有的定时器
      this.logRefreshInterval = setInterval(() => {
        if (this.logDialogVisible) {
          this.fetchLogs(false); // 静默刷新，不显示加载状态
        }
      }, 5000);
    },
    // 停止日志自动刷新
    stopLogAutoRefresh() {
      if (this.logRefreshInterval) {
        clearInterval(this.logRefreshInterval);
        this.logRefreshInterval = null;
      }
    },
    // 获取日志
    async fetchLogs(showLoading = true) {
      try {
        if (showLoading) {
          this.logLoading = true;
        }
        
        const params = new URLSearchParams({
          module: 'base',
          page: this.logCurrentPage,
          size: this.logPageSize
        });
        
        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        });
        
        if (!response.ok) throw new Error(this.$t('common.networkError'));
        
        const data = await response.json();
        
        if (data.errCode === 1000) {
          ElMessage.warning(this.$t('homePage.pleaseLoginFirst'));
          this.$router.push('/user-login');
          return;
        }
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || this.$t('homePage.fetchLogsFailed'));
        }
        
        this.logsData = data.data || [];
        this.logTotal = data.count || 0;
        
        // 只有在非自动刷新（即手动操作）时才滚动到顶部
        if (showLoading && this.$refs.logContentRef) {
          this.$refs.logContentRef.scrollTop = 0;
        }
      } catch (error) {
        console.error(this.$t('homePage.fetchLogsFailed') + ':', error);
        if (showLoading) {
          ElMessage.error(this.$t('homePage.fetchLogsError', { error: error.message || this.$t('homePage.networkError') }));
        }
      } finally {
        if (showLoading) {
          this.logLoading = false;
        }
      }
    },
    // 刷新日志
    refreshLogs() {
      this.fetchLogs(true);
    },
    // 清空日志
    async clearLogs() {
      try {
        await ElMessageBox.confirm(this.$t('homePage.confirmClearLogs'), this.$t('common.prompt'), {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
        
        this.logClearing = true;
        
        const params = new URLSearchParams({
          module: 'base'
        });
        
        const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        
        if (!response.ok) throw new Error(this.$t('common.networkError'));
        
        const data = await response.json();
        
        if (data.errCode === 1000) {
          ElMessage.warning(this.$t('homePage.pleaseLoginFirst'));
          this.$router.push('/user-login');
          return;
        }
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || this.$t('homePage.clearError'));
        }
        
        ElMessage.success(this.$t('homePage.logsCleared'));
        this.fetchLogs(true);
      } catch (error) {
        if (error !== 'cancel') {
          console.error(this.$t('homePage.clearError') + ':', error);
          ElMessage.error(this.$t('homePage.clearError') + ': ' + (error.message || this.$t('homePage.networkError')));
        }
      } finally {
        this.logClearing = false;
      }
    },
    // 下载所有日志
    async downloadAllLogs() {
      try {
        this.logDownloading = true;
        
        // 获取所有日志（设置一个足够大的size值）
        const params = new URLSearchParams({
          module: 'base',
          page: 1,
          size: 10000 // 设置一个较大的数值，获取尽可能多的日志
        });
        
        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        });
        
        if (!response.ok) throw new Error(this.$t('common.networkError'));
        
        const data = await response.json();
        
        if (data.errCode === 1000) {
          ElMessage.warning(this.$t('homePage.pleaseLoginFirst'));
          this.$router.push('/user-login');
          return;
        }
        
        if (data.errCode !== 0) {
          throw new Error(data.errMsg || this.$t('homePage.fetchLogsFailed'));
        }
        
        const allLogs = data.data || [];
        
        if (allLogs.length === 0) {
          ElMessage.warning(this.$t('homePage.noLogsToDownload'));
          return;
        }
        
        // 格式化所有日志内容
        let logContent = '';
        allLogs.forEach(log => {
          logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`;
        });
        
        // 创建下载链接
        const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `system_home_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        ElMessage.success(this.$t('homePage.downloadSuccess', { count: allLogs.length }));
      } catch (error) {
        console.error(this.$t('homePage.downloadError') + ':', error);
        ElMessage.error(this.$t('homePage.downloadError') + ': ' + (error.message || this.$t('homePage.networkError')));
      } finally {
        this.logDownloading = false;
      }
    },
    // 处理每页条数变化
    handleLogSizeChange(size) {
      this.logPageSize = size;
      this.logCurrentPage = 1;
      this.fetchLogs(true);
    },
    // 处理页码变化
    handleLogPageChange(page) {
      this.logCurrentPage = page;
      this.fetchLogs(true);
    }
  }
}
</script>

<style scoped>
/* 基础样式 */
:root {
  --primary-color: #4361ee;
  --secondary-color: #3f37c9;
  --success-color: #4cc9f0;
  --warning-color: #f8961e;
  --danger-color: #f94144;
  --light-color: #f8f9fa;
  --dark-color: #212529;
}

.home-page {
  padding: 24px;
  max-width: 100%;
  margin: 0 auto;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  color: var(--dark-color);
  font-size: 28px;
  font-weight: 600;
  margin: 0;
}

.refresh-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.last-update {
  color: #6c757d;
  font-size: 14px;
}

.refresh-btn, .log-btn {
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.refresh-btn {
  background-color: var(--primary-color);
  color: white;
}

.refresh-btn:hover:not(:disabled) {
  background-color: var(--secondary-color);
  transform: translateY(-1px);
}

.refresh-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.log-btn {
  background-color: var(--success-color);
  color: white;
}

.log-btn:hover {
  background-color: #3aa8d0;
  transform: translateY(-1px);
}

.refresh-icon, .log-icon {
  transition: transform 0.3s;
  font-style: normal;
}

.refresh-icon.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 卡片布局 */
.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(600px, 1fr));
  gap: 24px;
}

.info-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 24px;
  transition: transform 0.3s, box-shadow 0.3s;
}

.info-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 12px;
}

.title {
  color: var(--dark-color);
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-icon {
  font-size: 24px;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.uptime, .version {
  font-size: 14px;
  color: #6c757d;
  background: #f8f9fa;
  padding: 4px 8px;
  border-radius: 4px;
}

/* 信息项样式 */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.info-item {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s;
}

.info-item:hover {
  background-color: #e9ecef;
  transform: translateY(-2px);
}

.label {
  color: #495057;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
}

.value {
  color: var(--dark-color);
  font-size: 16px;
  font-weight: 500;
}

.highlight {
  color: var(--primary-color);
  font-weight: 600;
}

.clock {
  font-family: 'Courier New', monospace;
  font-size: 18px;
  color: var(--secondary-color);
}

/* 进度条样式 */
.progress-container {
  height: 8px;
  background-color: #e9ecef;
  border-radius: 4px;
  margin: 8px 0;
  overflow: hidden;
}

.progress-container.small {
  height: 6px;
}

.progress-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.5s ease;
}

.percent {
  font-size: 14px;
  color: #6c757d;
  margin-left: 8px;
}

.detail {
  font-size: 14px;
  color: #6c757d;
  margin-top: 4px;
  display: block;
}

/* 网络速度样式 */
.network-item {
  grid-column: span 1;
}

.network-speed {
  display: flex;
  gap: 16px;
}

.speed {
  font-family: 'Courier New', monospace;
  font-size: 15px;
  padding: 4px 8px;
  border-radius: 4px;
}

.upload {
  background-color: rgba(67, 97, 238, 0.1);
  color: var(--primary-color);
}

.download {
  background-color: rgba(76, 201, 240, 0.1);
  color: var(--success-color);
}

/* 磁盘样式 */
.disk-item {
  grid-column: span 1;
}

.disk-details {
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  font-size: 14px;
}

.space {
  color: #6c757d;
}

/* 日志对话框样式 */
.log-dialog :deep(.el-dialog__body) {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.log-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.log-toolbar .log-stats {
  display: flex;
  align-items: center;
  gap: 24px;
  font-size: 13px;
  color: #606266;
}

.log-toolbar .log-stats .auto-refresh-control {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 16px;
  border-left: 1px solid #dcdfe6;
}

.log-toolbar .log-stats .auto-refresh-control .refresh-label {
  font-size: 12px;
  color: #909399;
}

.log-toolbar .log-stats .auto-refresh-control .refresh-interval {
  font-size: 12px;
  color: #67c23a;
}

.log-toolbar .log-actions {
  display: flex;
  gap: 8px;
}

.download-icon, .clear-icon {
  font-style: normal;
  margin-right: 4px;
}

@media (max-width: 768px) {
  .log-toolbar {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  
  .log-toolbar .log-stats {
    width: 100%;
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .log-toolbar .log-stats .auto-refresh-control {
    padding-left: 0;
    border-left: none;
  }
  
  .log-toolbar .log-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 480px) {
  .log-toolbar .log-stats {
    font-size: 12px;
  }
}

.log-content {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
  margin-bottom: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.log-content .empty-log {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #909399;
}

.log-content .empty-log .empty-icon {
  font-size: 32px;
  margin-bottom: 12px;
  font-style: normal;
}

.log-content .empty-log p {
  margin: 0;
  font-size: 14px;
}

.log-content .log-list {
  padding: 12px;
}

.log-content .log-list .log-item {
  padding: 8px 12px;
  border-bottom: 1px solid #e4e7ed;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.log-content .log-list .log-item:last-child {
  border-bottom: none;
}

.log-content .log-list .log-item.log-level-error {
  background-color: #fef0f0;
}

.log-content .log-list .log-item.log-level-warn {
  background-color: #fdf6ec;
}

.log-content .log-list .log-item .log-time {
  color: #909399;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-content .log-list .log-item .log-level {
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-content .log-list .log-item .log-level.level-error {
  color: #f56c6c;
}

.log-content .log-list .log-item .log-level.level-warn {
  color: #e6a23c;
}

.log-content .log-list .log-item .log-level.level-info {
  color: #409eff;
}

.log-content .log-list .log-item .log-message {
  color: #303133;
  word-break: break-all;
  flex: 1;
}

@media (max-width: 768px) {
  .log-content {
    max-height: 350px;
  }
  
  .log-content .log-list {
    padding: 8px;
  }
  
  .log-content .log-list .log-item {
    font-size: 12px;
    flex-wrap: wrap;
    gap: 4px;
  }
  
  .log-content .log-list .log-item .log-time {
    width: 100%;
    margin-bottom: 2px;
  }
  
  .log-content .log-list .log-item .log-message {
    width: 100%;
    margin-left: 0;
  }
}

@media (max-width: 480px) {
  .log-content {
    max-height: 300px;
  }
  
  .log-content .log-list .log-item {
    font-size: 11px;
  }
}

.log-pagination {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .log-pagination {
    justify-content: center;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

@media (max-width: 480px) {
  .dialog-footer {
    flex-direction: column;
  }
  
  .dialog-footer .el-button {
    width: 100%;
    margin-left: 0 !important;
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
  
  .header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .network-item, .disk-item {
    grid-column: span 1;
  }
  
  .refresh-controls {
    width: 100%;
    justify-content: space-between;
  }
  
  .refresh-btn, .log-btn {
    flex: 1;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .home-page {
    padding: 16px;
  }
  
  .info-card {
    padding: 16px;
  }
  
  .refresh-controls {
    flex-wrap: wrap;
  }
  
  .last-update {
    width: 100%;
    text-align: center;
  }
}

:deep(.el-switch) {
  @media (max-width: 768px) {
    .el-switch__core {
      width: 36px !important;
      height: 18px !important;
      
      &:after {
        width: 14px !important;
        height: 14px !important;
      }
    }
  }
}
</style>